import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@components/Layout";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@components/ui/table";
import { Button } from "@components/ui/button";
import { chipClass, MICRO_LABEL, CARD_CLASS, type ChipTone } from "@components/cockpit";
import { stressToneForPercentile, stressBandForPercentile } from "@components/cockpit/stress";
import { API_BASE_URL } from "@lib/config";
import { useUser } from "../hooks/useUser";
import { useLivePoll } from "../hooks/useLivePoll";
import { fmtLocalDateTime } from "@lib/dateUtils";
import { RiskMethodologyModal } from "@components/RiskMethodologyModal";
import { FIXED_INCOME_METHODOLOGY } from "@components/fixedIncomeMethodologyContent";
import { fiFreshness, fredSourceBanner } from "@lib/fiFreshness";
import { inactiveRoleNote, sleeveComposition as buildSleeveComposition } from "@lib/fiSleeveComposition";
import { lineageView, rateTrendTile, rearmTile, tapeLineText, wideCreditTile, type Lineage, type TapeLine } from "@lib/fiSignalTiles";
import { navUnreadNote, syncSuccessMessage, type RefreshPortfolioResponse } from "@lib/refreshPortfolio";
import { WhyTheseTargets } from "@components/fi/WhyTheseTargets";
import { IncomeCalendarCard } from "@components/fi/IncomeCalendarCard";
import { stalenessTitle, type FiProfileStaleness } from "@lib/fiProfileStaleness";
import {
  activeAdjustments,
  capNote,
  gapCauses,
  planSummary,
  sharesLine,
  sharesLineRaw,
  bandHeadline,
  timerRunBanner,
  planBasisNote,
  heldBuySummary,
  type TimerRun,
  type PrincipalBreach,
  type RebalancePlan,
  type RebalanceRung,
  type TargetAttribution,
} from "@lib/fiAttribution";

interface InflationTelemetry {
  enabled: boolean;
  state: string;
  t10yie_bps?: number;
  t10yie_med3y_bps?: number;
  t10yie_p80_bps?: number;
  t10yie_slope20_bps?: number;
  t5y5y_confirm?: boolean;
  etf?: string;
  caps?: { min: number; max: number };
  thresholds_bps?: { watch_on?: number; surge_on?: number };
  targets?: { watch: number; surge: number };
}

interface MomentumMeta {
  enabled?: boolean;
  credit_spread_roc_bps?: number | null;
  curve_slope_change_bps?: number | null;
  move_pct_current?: number | null;
  move_pct_prev?: number | null;
  move_pct_momentum?: number | null;
  roc_trading_days?: number | null;
  applied?: boolean;
  scale?: number;
  triggers?: {
    credit_spread_momentum_negative?: boolean;
    curve_flattening_accelerating?: boolean;
    move_rising_from_bottom_decile?: boolean;
    [k: string]: boolean | undefined;
  };
  deltas_applied?: Record<string, number>;
  [k: string]: any;
}

interface RateTrendMeta {
  enabled?: boolean;
  available?: boolean;
  applied?: boolean;
  state?: string;
  series_id?: string;
  current_bps?: number | null;
  change_short_bps?: number | null;
  change_long_bps?: number | null;
  short_window_days?: number | null;
  long_window_days?: number | null;
  trigger_short_bps?: number | null;
  trigger_long_bps?: number | null;
  scale?: number | null;
  deltas_applied?: Record<string, number>;
  [k: string]: any;
}

interface PortfolioRiskMeta {
  estimated_duration_years?: number | null;
  duration_budget_years?: number | null;
  regime?: string;
  duration_contribution_years?: Record<string, number>;
  // Held-duration block (backend build_held_duration_telemetry). estimated_duration_years
  // above is TARGET-weight based; these are the rate risk actually on the book.
  held_duration_years?: number | null;
  held_duration_covered_sum_years?: number | null;
  held_duration_contribution_years?: Record<string, number>;
  held_duration_sources?: Record<string, string>;
  held_duration_coverage?: {
    covered_weight?: number | null;
    missing_tickers?: string[];
    fallback_tickers?: string[];
    all_covered?: boolean;
    note?: string;
  };
  instrument_roles?: Record<string, {
    risk_bucket?: string | null;
    principal_role?: string | null;
    duration_years?: number | null;
  }>;
  [k: string]: any;
}

interface PrincipalDrawdownMeta {
  enabled?: boolean;
  available?: boolean;
  applied?: boolean;
  state?: string;
  breaches?: Record<string, { return_pct?: number; reasons?: string[]; [k: string]: any }>;
  caps_applied?: Record<string, { previous_weight?: number; cap_weight?: number; reallocated_weight?: number }>;
  deltas_applied?: Record<string, number>;
  /** Zero-cap lockout (2026-09-15): 0%-capped rungs the stop sold to zero, held at 0% until free_from. */
  zero_cap_lockout?: {
    enabled?: boolean;
    days?: number;
    locks?: Record<string, { since: string; locked_through: string; free_from: string; new?: boolean }>;
    conflicts?: string[];
  } | null;
  [k: string]: any;
}

interface CreditRegimeMeta {
  active?: boolean;
  state?: string;            // detailed: Calm / Defensive-FALLING / Defensive-RISING / Re-arm
  regime?: string;           // collapsed: Calm / Defensive / Re-arm
  held?: boolean;
  hold_reason?: string | null;
  explain?: string;
  computed_at_utc?: string;
  duration_regime_key?: string;
  duration_cap_years?: number | null;
  signals?: {
    wide_credit?: { is_wide?: boolean; current?: number | null; threshold?: number | null; percentile?: number; current_percentile?: number | null; history_start?: string | null; available?: boolean };
    rate_trend?: { direction?: string; current?: number | null; ma?: number | null; band_bps?: number; in_deadband?: boolean; prior_direction?: string | null; available?: boolean };
    rearm?: { is_tightening?: boolean; roc_bps?: number | null; tighten_bps?: number; available?: boolean };
  };
  fetched?: { baa10y_last?: string; dgs10_last?: string; baa10y_age_bd?: number; dgs10_age_bd?: number };
  since_utc?: string | null;
  persisted_regime?: string | null;
  persisted_detail?: string | null;
  /** Which engine run the signals describe + freshness checks (docs/HANDOFF_FI_SIGNAL_LINEAGE.md). */
  lineage?: Lineage | null;
  /** CIO D4: IBKR indicative 10Y — display only, never part of the signal. */
  tape_10y_indicative?: TapeLine | null;
  error?: string;
}

interface FiYieldMeta {
  available?: boolean;
  sleeve_yield_pct?: number | null;
  covered_weight?: number;
  per_etf_yield_pct?: Record<string, number>;
  anchor_date?: string;
  excluded?: string[];
  staleness?: FiProfileStaleness | null;
}

interface FIMeta {
  fixed_income_nav?: number;
  credit_regime?: CreditRegimeMeta | null;
  fi_yield?: FiYieldMeta | null;

  current_weights: Record<string, number>;
  target_weights: Record<string, number>;
  weight_diff?: Record<string, number>;

  holdings_market_value?: Record<string, number>;
  holdings?: Array<{
    ticker: string;
    quantity: number;
    market_value: number;
    cost_basis: number;
    holding_type?: string | null;
    role?: string | null;
    weight_pct: number;
    account_id?: string | null;
  }>;

  vix_zscore?: number;
  vix_value?: number;
  vix_spot?: number;

  move_index_bps?: number;
  move_index?: number;
  move_index_percentile?: number;
  yield_curve_bps?: number;
  yield_curve?: number;
  credit_oas_bps?: number;
  credit_spread?: number;

  momentum?: MomentumMeta;
  rate_trend?: RateTrendMeta;
  portfolio_risk?: PortfolioRiskMeta;
  principal_drawdown_overlay?: PrincipalDrawdownMeta;
  // Why the targets are what they are (regime base -> overlays -> principal defense), and the
  // whole-share checklist. Both computed by the backend; see @lib/fiAttribution.
  target_attribution?: TargetAttribution | null;
  rebalance_plan?: RebalancePlan | null;
  // Execution discipline (2026-09-15): the live-refresh plan (secondary) and the 15:15 MT run the checklist trades off.
  rebalance_plan_live?: RebalancePlan | null;
  timer_run?: TimerRun | null;

  regime: string;
  inflation_state?: string;
  inflation_telemetry?: InflationTelemetry;

  as_of_utc?: string;
  computed_at_utc?: string;

  [k: string]: any;
}

interface NavLatest {
  nav?: {
    TotalNAV?: number | null;
    CashNAV?: number | null;
    AsOfDate?: string | null;
  } | null;
  total_nav?: number | null;
  cash_nav?: number | null;
  as_of?: string | null;
  asOfUTC?: string | null;
  source?: string | null;
  cashOverride?: any;
  cash_override?: any;
}

const fmtDollar = (n?: number) =>
  n == null || Number.isNaN(n)
    ? "—"
    : `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;

// Signed dollar, e.g. "+$12,345" (under target) / "-$6,789" (excess). Mirrors
// the Diff % sign convention: positive = under-allocated, negative = over.
const fmtSignedDollar = (n?: number) => {
  if (n == null || Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const fmtPct = (n?: number) =>
  n == null || Number.isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`;

const fmtNumber = (n?: number, digits = 2) =>
  n == null || Number.isNaN(n) ? "—" : n.toFixed(digits);

const yesNo = (b?: boolean) => (b == null ? "—" : b ? "Yes" : "No");

const fmtUtc = (iso?: string) => fmtLocalDateTime(iso);
// A YYYY-MM-DD observation date as "9/10" — anchored at noon so no time zone can shift the day.
const fmtObsDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) : "—";

const toNum = (v: unknown): number | undefined => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
};

const pickNum = (obj: unknown, ...keys: string[]): number | undefined => {
  const o = obj as Record<string, unknown> | null | undefined;
  if (!o) return undefined;
  for (const k of keys) {
    const n = toNum(o[k]);
    if (n != null) return n;
  }
  return undefined;
};

const normalizeLatestNav = (json: any): NavLatest | null => {
  if (!json || typeof json !== "object") return null;
  const navObj = json?.nav ?? json?.latest_nav?.nav ?? null;
  const cashOverride = json?.cashOverride ?? json?.cash_override ?? json?.latest_nav?.cashOverride ?? json?.latest_nav?.cash_override ?? null;
  return {
    nav: navObj,
    total_nav:
      typeof navObj?.TotalNAV === "number" ? navObj.TotalNAV : (typeof json?.total_nav === "number" ? json.total_nav : null),
    cash_nav:
      typeof navObj?.CashNAV === "number" ? navObj.CashNAV : (typeof json?.cash_nav === "number" ? json.cash_nav : null),
    as_of:
      typeof json?.as_of === "string"
        ? json.as_of
        : typeof json?.asOfUTC === "string"
        ? json.asOfUTC
        : typeof navObj?.AsOfDate === "string"
        ? navObj.AsOfDate
        : null,
    asOfUTC: typeof json?.asOfUTC === "string" ? json.asOfUTC : null,
    source: typeof json?.source === "string" ? json.source : null,
    cashOverride: cashOverride ?? null,
    cash_override: cashOverride ?? null,
  };
};

const detectTipsTicker = (meta?: FIMeta): string | undefined => {
  if (!meta) return undefined;

  const tele = meta.inflation_telemetry;
  if (tele?.etf && typeof tele.etf === "string") return tele.etf.toUpperCase();

  const inTargets = new Set(Object.keys(meta.target_weights ?? {}));
  const inCurrent = new Set(Object.keys(meta.current_weights ?? {}));
  const present = (t: string) => inTargets.has(t) || inCurrent.has(t);

  if (present("STIP")) return "STIP";
  if (present("VTIP")) return "VTIP";
  return undefined;
};

// `short` drops the "Inflation" prefix for use inside the inflation card, where the
// surrounding label already supplies that context.
const overlayFromStateString = (state?: string) => {
  const s = (state || "").toLowerCase();
  if (s.includes("surge"))
    return { label: "Inflation overlay: Active", short: "Overlay: Active", tone: "red" as const };
  if (s.includes("watch"))
    return { label: "Inflation overlay: Watch", short: "Overlay: Watch", tone: "amber" as const };
  return { label: "Inflation overlay: Off", short: "Overlay: Off", tone: "gray" as const };
};

const overlayFromTargets = (meta?: FIMeta) => {
  if (!meta) return overlayFromStateString(undefined);

  const tipTicker = detectTipsTicker(meta);
  if (!tipTicker) return overlayFromStateString(meta.inflation_state);

  const t = meta.target_weights?.[tipTicker];
  const watchT = meta.inflation_telemetry?.targets?.watch;
  const surgeT = meta.inflation_telemetry?.targets?.surge;
  const eps = 1e-6;

  if (typeof t === "number") {
    if (typeof surgeT === "number" && t >= surgeT - eps) {
      return { label: "Inflation overlay: Active", short: "Overlay: Active", tone: "red" as const };
    }
    if (typeof watchT === "number" && t >= watchT - eps) {
      return { label: "Inflation overlay: Watch", short: "Overlay: Watch", tone: "amber" as const };
    }
    // At/below the STIP structural baseline the overlay isn't lifting anything — show Off, not "On".
    return { label: "Inflation overlay: Off", short: "Overlay: Off", tone: "gray" as const };
  }

  return overlayFromStateString(meta.inflation_state);
};

/** =======================
 *  Theme-safe UI helpers
 *  ======================= */
const ui = {
  pageBg: "var(--bg-primary)",
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  tableBg: "var(--card-bg)",
  tableHeaderBg: "var(--nav-bg)",
  tableHeaderText: "var(--nav-text)",
  hoverBg: "rgba(148, 163, 184, 0.08)",
  groupBg: "rgba(148, 163, 184, 0.10)",
  inputBg: "var(--card-bg)",
  shadow: "var(--shadow-soft)",
};

const themeCardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const themeInputStyle: React.CSSProperties = {
  backgroundColor: ui.inputBg,
  color: ui.textPrimary,
  border: `1px solid ${ui.cardBorder}`,
};

const smallMutedText: React.CSSProperties = {
  color: ui.textMuted,
};

const secondaryText: React.CSSProperties = {
  color: ui.textSecondary,
};

// FI's legacy tone names map onto the canonical chip tones (one source of truth).
const PILL_TONE: Record<string, ChipTone> = {
  red: "danger",
  amber: "warning",
  gray: "neutral",
  green: "positive",
  blue: "info",
};
const Pill: React.FC<{
  tone: "red" | "amber" | "gray" | "green" | "blue";
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span className={chipClass(PILL_TONE[tone] ?? "neutral")}>{children}</span>
);

// FI v3 ballast-state presentation (detailed state -> plain-English label, tone, one-liner)
const V3_STATE_DISPLAY: Record<string, { label: string; tone: ChipTone; blurb: string }> = {
  Calm: { label: "Calm", tone: "positive", blurb: "Credit benign — the regime base is the Calm floating mix: cash, floating-rate and short carry, with no IG-credit or duration legs" },
  "Defensive-FALLING": { label: "Defensive · Falling Rates", tone: "info", blurb: "Wide credit + rates easing — hold duration (flight-to-quality)" },
  "Defensive-RISING": { label: "Defensive · Rising Rates", tone: "warning", blurb: "Wide credit + rate shock — short / cash" },
  "Re-arm": { label: "Re-Arming", tone: "info", blurb: "Spreads still wide but tightening — re-entering credit for the recovery" },
};
const v3StateDisplay = (state?: string) =>
  V3_STATE_DISPLAY[state ?? ""] ?? { label: state || "—", tone: "neutral" as ChipTone, blurb: "" };

type SortKey = "etf" | "current" | "target" | "diff" | "notionalAbs";

const FixedIncomeView: React.FC = () => {
  const { userLoading } = useUser();

  const [meta, setMeta] = useState<FIMeta | null>(null);
  const [latestNav, setLatestNav] = useState<NavLatest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Broker-sync staleness, kept OUT of `error`: fetchFISnapshot() sets error to null as it
   * starts, so a warning written just before a reload is erased. Cleared when a new sync
   * STARTS, never when a reload finishes. */
  const [syncWarn, setSyncWarn] = useState<string | null>(null);
  // M3 Step 1: the Sync success line (holdings refreshed; the official NAV is the scheduled jobs').
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const [showTelemetry, setShowTelemetry] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshEverySec, setRefreshEverySec] = useState(1800);
  const [lastFetchedLocal, setLastFetchedLocal] = useState<string | null>(null);

  const [weightsFilter, setWeightsFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("diff");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const inFlight = useRef<AbortController | null>(null);
  const lastFetchMsRef = useRef<number>(0);

  const fetchFISnapshot = async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    setLoading(true);
    setError(null);

    try {
      const [fiRes, navJson] = await Promise.all([
        fetch(`${API_BASE_URL}/get_fixed_income_snapshot`, {
          signal: controller.signal,
        }),
        (async () => {
          try {
            const v2 = await fetch(`${API_BASE_URL}/risk/nav_latest`, { signal: controller.signal });
            if (v2.ok) return await v2.json();
          } catch (e: any) {
            if (e?.name === "AbortError") throw e;
          }
          try {
            const legacy = await fetch(`${API_BASE_URL}/get_portfolio_nav_latest`, { signal: controller.signal });
            if (legacy.ok) return await legacy.json();
          } catch (e: any) {
            if (e?.name === "AbortError") throw e;
          }
          return null;
        })(),
      ]);
      if (!fiRes.ok) throw new Error(`HTTP ${fiRes.status} – ${fiRes.statusText}`);
      const data: any = await fiRes.json();
      setLatestNav(normalizeLatestNav(navJson));

      const rawMeta: FIMeta = (data.meta ?? data.fixed_income_meta ?? data) as any;

      if (rawMeta && !rawMeta.weight_diff) {
        const wd: Record<string, number> = {};
        const keys = new Set([
          ...Object.keys(rawMeta.current_weights ?? {}),
          ...Object.keys(rawMeta.target_weights ?? {}),
        ]);
        keys.forEach((k) => {
          const cur = toNum(rawMeta.current_weights?.[k]) ?? 0;
          const tgt = toNum(rawMeta.target_weights?.[k]) ?? 0;
          wd[k] = tgt - cur;
        });
        rawMeta.weight_diff = wd;
      } else if (rawMeta?.weight_diff) {
        const wd: Record<string, number> = {};
        const obj = rawMeta.weight_diff as Record<string, unknown>;
        for (const [k, v] of Object.entries(obj)) wd[k] = toNum(v) ?? 0;
        rawMeta.weight_diff = wd;
      }

      if (rawMeta?.momentum) {
        const m = rawMeta.momentum as Record<string, unknown>;
        const normM: MomentumMeta = {
          enabled: Boolean(m.enabled),
          credit_spread_roc_bps: toNum(m.credit_spread_roc_bps) ?? null,
          curve_slope_change_bps: toNum(m.curve_slope_change_bps) ?? null,
          move_pct_current: toNum(m.move_pct_current) ?? null,
          move_pct_prev: toNum(m.move_pct_prev) ?? null,
          move_pct_momentum: toNum(m.move_pct_momentum) ?? null,
          roc_trading_days: toNum(m.roc_trading_days) ?? null,
          applied: typeof m.applied === "boolean" ? (m.applied as boolean) : undefined,
          scale: toNum(m.scale),
          triggers: (m.triggers as MomentumMeta["triggers"]) ?? undefined,
          deltas_applied:
            m.deltas_applied && typeof m.deltas_applied === "object"
              ? (Object.fromEntries(
                  Object.entries(m.deltas_applied as Record<string, unknown>).map(([k, v]) => [
                    k,
                    toNum(v) ?? 0,
                  ])
                ) as Record<string, number>)
              : undefined,
        };
        rawMeta.momentum = normM;
      }

      setMeta(rawMeta);

      setLastFetchedLocal(new Date().toISOString());
      lastFetchMsRef.current = Date.now();
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e?.name === "AbortError") return;
      console.error("Error fetching FI snapshot:", err);
      setError(e?.message || "Unknown error");
    } finally {
      setLoading(false);
      inFlight.current = null;
    }
  };

  // "Sync IBKR + Refresh": runs the heavy server-side IBKR -> SQL sync (positions incl. FI holdings +
  // NAV), then reloads the FI snapshot from the canonical endpoint — the same path the plain Refresh
  // button uses. Read-only sync: it never places, modifies, or cancels any orders.
  const syncIbkrAndRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      let syncOk = false;
      setSyncWarn(null); // a new attempt supersedes the last verdict
      setSyncNote(null);
      try {
        // Refreshes holdings only: the official daily NAV is written by the scheduled jobs and
        // the 14:05 MT close mark is final (M3 Step 1, 2026-09-25).
        const res = await fetch(`${API_BASE_URL}/refresh_portfolio`, {
          method: "POST",
          headers: { Accept: "application/json" },
        });
        // HTTP 200 IS NOT SUCCESS HERE. refresh_portfolio degrades to last-known data and
        // still answers 200 when the IBKR sync fails, so `res.ok` alone showed a clean
        // tick over stale holdings — the same class of silent success as the
        // sync_ibkr_positions 404 documented below. Keying off the explicit syncOk /
        // sync.error the backend returns is the guard PortfolioView has carried since the
        // 2026-07-01 audit (P1 #24); it was never ported here. (CIO decision Q5,
        // 2026-08-27.)
        let body: RefreshPortfolioResponse | null = null;
        try {
          body = await res.clone().json();
        } catch {
          /* non-JSON body — fall back to the status code alone */
        }
        const reportedFailure = body?.syncOk === false || Boolean(body?.sync?.error);
        syncOk = res.ok && !reportedFailure;
        if (res.ok && reportedFailure) {
          const detail =
            body?.sync?.error ||
            (Array.isArray(body?.errors) ? (body?.errors ?? []).join("; ") : "IBKR sync failed");
          setSyncWarn(`IBKR sync failed — the holdings below are last-known, not fresh. ${detail}`);
        } else if (syncOk) {
          const synced = typeof body?.synced === "number" ? body.synced : null;
          setSyncNote(syncSuccessMessage(synced));
          // Amber, never a failure: syncOk alone decides success.
          setSyncWarn(navUnreadNote(body));
        }
      } catch {
        // route/network failure — fall through to the lighter positions-only sync
      }
      if (!syncOk) {
        // Route is `refresh-positions` (refresh_positions_http). This previously posted
        // to `sync_ibkr_positions`, which IS NOT A ROUTE -- the 404 was swallowed and the
        // reload below re-rendered the same stale holdings, so a failed sync looked
        // identical to a successful one. (2026-07-30 audit Tier-0 #13.)
        try {
          const fb = await fetch(`${API_BASE_URL}/refresh-positions`, { method: "POST" });
          syncOk = fb.ok;
          if (!fb.ok) {
            setSyncWarn(`Positions sync failed (${fb.status}). Holdings below may be STALE.`);
          }
        } catch (fbErr: unknown) {
          const m = (fbErr as { message?: string })?.message ?? "network error";
          setSyncWarn(`Positions sync unreachable (${m}). Holdings below may be STALE.`);
        }
      }
      await fetchFISnapshot();
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error("IBKR sync failed:", e);
      setError(err?.message ?? "IBKR sync failed");
    } finally {
      setLoading(false);
    }
  };

  // Export the FI weights / transition table exactly as computed to a CSV the CIO downloads locally
  // (records / audit). Raw numeric values; no trade affordance; the data never leaves the browser.
  const handleExportCsv = () => {
    try {
      const cols: Array<[string, (r: (typeof weightsRows)[number]) => string]> = [
        ["ETF", (r) => r.etf],
        ["Role", (r) => roleOf(r.etf).replace(/_/g, " ")],
        ["Yield_est_pct", (r) => (perEtfYield[r.etf] != null ? perEtfYield[r.etf].toFixed(3) : "")],
        ["DurContrib_yrs", (r) => { const d = durContribOf(r.etf); return d != null ? d.toFixed(4) : ""; }],
        ["Current_pct", (r) => (r.current * 100).toFixed(2)],
        ["Target_pct", (r) => (r.target * 100).toFixed(2)],
        ["Diff_pct", (r) => (r.diff * 100).toFixed(2)],
        ["Diff_usd", (r) => (r.diffDollar != null ? r.diffDollar.toFixed(0) : "")],
        ["Notional_usd", (r) => (r.marketValue != null ? r.marketValue.toFixed(0) : "")],
        // Whole-share checklist from the backend plan (advisory; last IBKR mark, not a live quote).
        ["Action", (r) => planByTicker.get(r.etf)?.action ?? ""],
        ["Shares", (r) => { const p = planByTicker.get(r.etf); return p?.shares != null ? String(p.shares) : ""; }],
        ["Mark_price", (r) => { const p = planByTicker.get(r.etf); return p?.price != null ? p.price.toFixed(4) : ""; }],
      ];
      const header = cols.map((c) => c[0]).join(",");
      const lines = weightsRows.map((r) => cols.map((c) => `"${c[1](r).replace(/"/g, '""')}"`).join(","));
      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fi_targets_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed:", e);
    }
  };

  useEffect(() => {
    if (!userLoading) fetchFISnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  // Auto-refresh (CIO, 2026-09-14): every 30 min by default, paused while the tab is hidden, never
  // stacked on a request still running. The page's inputs change about four times a day (holdings
  // marks 06:15 / 06:45 / 13:45 MT, FRED once a day, the engine timer 15:15 MT) and each refresh is
  // a ~20s server computation with dozens of FRED calls, so the old 5-minute poll mostly re-fetched
  // identical numbers while making the header look minutes-fresh. Returning to the tab refreshes only
  // if the last successful load is more than 5 minutes old.
  useLivePoll(
    () => {
      if (Date.now() - lastFetchMsRef.current < 5 * 60_000) return;
      return fetchFISnapshot();
    },
    refreshEverySec * 1000,
    { enabled: autoRefresh && !userLoading, immediate: false },
  );

  const inflEtf = meta?.inflation_telemetry?.enabled ? detectTipsTicker(meta ?? undefined) : undefined;

  const vixSpot = pickNum(meta ?? {}, "vix_value", "vix_spot");
  const moveBps = pickNum(meta ?? {}, "move_index_bps", "move_index");
  const movePct = pickNum(meta ?? {}, "move_index_percentile");
  const curveBps = pickNum(meta ?? {}, "yield_curve_bps", "yield_curve");
  const oasBps = pickNum(meta ?? {}, "credit_oas_bps", "credit_spread");

  const infl = overlayFromTargets(meta ?? undefined);

  const tipTicker = detectTipsTicker(meta ?? undefined);
  const tipTarget = tipTicker && meta?.target_weights ? meta.target_weights[tipTicker] : undefined;
  const tipCurrent = tipTicker && meta?.current_weights ? meta.current_weights[tipTicker] : undefined;
  const showTipsLine = tipTicker && (typeof tipTarget === "number" || typeof tipCurrent === "number");

  const holdingsMV = meta?.holdings_market_value ?? {};
  const nav = meta?.fixed_income_nav;

  const totalNav = (() => {
    const n = latestNav?.nav;
    return (typeof n?.TotalNAV === "number" ? n.TotalNAV : (typeof latestNav?.total_nav === "number" ? latestNav.total_nav : null));
  })();

  const observedCash = (() => {
    const n = latestNav?.nav;
    return (typeof n?.CashNAV === "number" ? n.CashNAV : (typeof latestNav?.cash_nav === "number" ? latestNav.cash_nav : 0)) || 0;
  })();

  const cashOverrideState = (() => {
    const raw = latestNav?.cashOverride ?? latestNav?.cash_override ?? null;
    const reserved = typeof raw?.reserved_cash === "number" ? raw.reserved_cash : observedCash;
    const enabled = !!(
      raw && (
        raw.enabled === true ||
        raw.mode === "reserved" ||
        raw.mode === "constrained" ||
        (typeof reserved === "number" && reserved > 0)
      )
    );
    return {
      enabled,
      reserved,
      mode: typeof raw?.mode === "string" ? raw.mode : (enabled ? "reserved" : "normal"),
    };
  })();

  const fiTargetTotalNav = typeof totalNav === "number" ? totalNav * 0.60 : null;
  const fiActualTotalNavPct = typeof totalNav === "number" && totalNav > 0 && typeof nav === "number" ? nav / totalNav : null;
  const fiTargetFillPct = typeof fiTargetTotalNav === "number" && fiTargetTotalNav > 0 && typeof nav === "number" ? nav / fiTargetTotalNav : null;

  // Basis: UNFILTERED meta.current_weights / meta.target_weights. Every aggregate on this page
  // (held duration, credit exposure, sleeve composition, transition checklist) must roll up from
  // these rows. The Weights search box narrows the TABLE only — it used to feed the aggregates
  // too, so typing "VG" made "Credit exposure" read 0.0% current against a live non-zero target
  // (FI target-vs-held audit 2026-08-18, item 6). Do not point a roll-up at weightsRows.
  const weightsRowsAll = useMemo(() => {
    if (!meta) return [];

    const keys = new Set<string>();
    Object.keys(meta.target_weights ?? {}).forEach((k) => keys.add(k));
    Object.keys(meta.current_weights ?? {}).forEach((k) => keys.add(k));
    Object.keys(holdingsMV ?? {}).forEach((k) => keys.add(k));

    // FI-sleeve weights are fractions of the FI notional (MV), so the dollar
    // drift is diff × FI NAV = target MV − current MV. Undefined if NAV missing.
    const navBase = toNum(meta.fixed_income_nav);

    const rows = Array.from(keys).map((etf) => {
      const cur = toNum(meta.current_weights?.[etf]) ?? 0;
      const tgt = toNum(meta.target_weights?.[etf]) ?? 0;
      const diff = toNum(meta.weight_diff?.[etf]) ?? (tgt - cur);

      const mv = toNum(holdingsMV?.[etf]);
      const mvAbs = mv == null ? 0 : Math.abs(mv);

      const diffDollar = navBase != null ? diff * navBase : undefined;

      return {
        etf,
        current: cur,
        target: tgt,
        diff,
        diffDollar,
        marketValue: mv,
        notionalAbs: mvAbs,
      };
    });

    return rows;
  }, [meta, holdingsMV]);

  // Filtered + sorted VIEW of the rows above — the Weights table (and its CSV export of that
  // table) only. Never use this for an aggregate: the search box would silently shrink it.
  const weightsRows = useMemo(() => {
    const q = weightsFilter.trim().toUpperCase();
    const filtered = q ? weightsRowsAll.filter((r) => r.etf.includes(q)) : weightsRowsAll;

    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === "etf") return a.etf.localeCompare(b.etf) * dir;
      if (sortKey === "current") return (a.current - b.current) * dir;
      if (sortKey === "target") return (a.target - b.target) * dir;
      if (sortKey === "notionalAbs") return (a.notionalAbs - b.notionalAbs) * dir;
      return (a.diff - b.diff) * dir;
    });

    return sorted;
  }, [weightsRowsAll, weightsFilter, sortKey, sortDir]);

  const driftAbsSum = useMemo(() => {
    if (!meta?.weight_diff) return undefined;
    const vals = Object.values(meta.weight_diff) as number[];
    if (!vals.length) return 0;
    return vals.reduce((acc: number, v: number) => acc + Math.abs(v || 0), 0);
  }, [meta?.weight_diff]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  // meta.timestampUTC is what get_fixed_income_snapshot actually emits; without it in
  // this chain the "as of" and stale-warning silently fell back to the browser clock
  // and never fired (2026-07-01 audit P1 #8).
  const asOfUtc = meta?.as_of_utc ?? meta?.computed_at_utc ?? meta?.timestampUTC ?? lastFetchedLocal ?? undefined;
  // When each input is actually FROM — marks, market series, VIX close, regime state — rather than when
  // the page last asked. See @lib/fiFreshness.
  const freshness = fiFreshness(meta, Date.now());
  // FRED down -> the backend serves its last-good stored copy and says so (CIO 2026-10-21).
  const fredBanner = fredSourceBanner(meta);

  const mom = meta?.momentum;
  const momEnabled = Boolean(mom?.enabled);
  const momApplied = typeof mom?.applied === "boolean" ? mom.applied : undefined;

  const inferredTriggers = useMemo(() => {
    if (!momEnabled) return null;

    const creditMin = 15.0;
    const curveMax = -15.0;
    const moveBottom = 0.10;
    const moveMomMin = 0.05;

    const credit = typeof mom?.credit_spread_roc_bps === "number" ? mom.credit_spread_roc_bps : null;
    const curve = typeof mom?.curve_slope_change_bps === "number" ? mom.curve_slope_change_bps : null;
    const moveCur = typeof mom?.move_pct_current === "number" ? mom.move_pct_current : null;
    const moveMom = typeof mom?.move_pct_momentum === "number" ? mom.move_pct_momentum : null;

    const tCredit = credit != null ? credit >= creditMin : false;
    const tCurve = curve != null ? curve <= curveMax : false;
    const tMove = moveCur != null && moveMom != null ? (moveCur <= moveBottom && moveMom >= moveMomMin) : false;

    return {
      credit_spread_momentum_negative: tCredit,
      curve_flattening_accelerating: tCurve,
      move_rising_from_bottom_decile: tMove,
    };
  }, [momEnabled, mom?.credit_spread_roc_bps, mom?.curve_slope_change_bps, mom?.move_pct_current, mom?.move_pct_momentum]);

  const triggers = mom?.triggers ?? inferredTriggers ?? null;
  const triggersFired = triggers ? Object.values(triggers).filter(Boolean).length : 0;

  const momTone: "gray" | "green" | "amber" = !momEnabled
    ? "gray"
    : triggersFired >= 2
    ? "amber"
    : triggersFired === 1
    ? "green"
    : "gray";

  const momLabel = !momEnabled
    ? "Momentum overlay: Off"
    : triggersFired > 0
    ? `Momentum overlay: Active (${triggersFired}/3)`
    : "Momentum overlay: On";

  const momTooltip = useMemo(() => {
    if (!momEnabled) return undefined;

    const lines: string[] = [];
    lines.push("Delta-of-Delta diagnostics (approx 3 months):");
    if (typeof mom?.credit_spread_roc_bps === "number") lines.push(`• Credit spread ROC: ${mom.credit_spread_roc_bps.toFixed(1)} bps`);
    if (typeof mom?.curve_slope_change_bps === "number") lines.push(`• Curve slope change: ${mom.curve_slope_change_bps.toFixed(1)} bps`);
    if (typeof mom?.move_pct_current === "number") lines.push(`• MOVE pct (current): ${(mom.move_pct_current * 100).toFixed(0)}%`);
    if (typeof mom?.move_pct_momentum === "number") lines.push(`• MOVE pct momentum: ${(mom.move_pct_momentum * 100).toFixed(0)} pts`);
    if (triggers) {
      lines.push("Triggers:");
      lines.push(`• Credit widening: ${triggers.credit_spread_momentum_negative ? "Yes" : "No"}`);
      lines.push(`• Curve flattening accel: ${triggers.curve_flattening_accelerating ? "Yes" : "No"}`);
      lines.push(`• MOVE rising from bottom decile: ${triggers.move_rising_from_bottom_decile ? "Yes" : "No"}`);
    }
    if (typeof momApplied === "boolean") {
      lines.push(`Applied: ${momApplied ? "Yes" : "No"}`);
    }
    if (typeof mom?.scale === "number") {
      lines.push(`Scale: ${mom.scale.toFixed(2)}`);
    }
    return lines.join("\n");
  }, [momEnabled, mom, triggers, momApplied]);

  const rateTrend = meta?.rate_trend;
  const portfolioRisk = meta?.portfolio_risk;
  const principalDefense = meta?.principal_drawdown_overlay;

  // Why the targets are what they are, and the whole-share plan — both from the backend (@lib/fiAttribution).
  const targetAttribution: TargetAttribution | null = meta?.target_attribution ?? null;
  const rebalancePlan: RebalancePlan | null = meta?.rebalance_plan ?? null;
  const planRungs: RebalanceRung[] = rebalancePlan?.available ? rebalancePlan.rungs ?? [] : [];
  const planByTicker = new Map(planRungs.map((r) => [r.ticker, r] as const));
  const planMarketValue = rebalancePlan?.available ? rebalancePlan.sleeve_market_value ?? 0 : 0;
  // Execution discipline (docs/HANDOFF_FI_EXECUTION_DISCIPLINE.md): the checklist trades off the 15:15 MT run and
  // says whether the tested strategy's 3% band would trade; intraday differences are information.
  const rebalancePlanLive: RebalancePlan | null = meta?.rebalance_plan_live ?? null;
  const timerRun: TimerRun | null = meta?.timer_run ?? null;
  const bandInfo = bandHeadline(rebalancePlan?.execution_band);
  const timerBanner = timerRunBanner(timerRun);
  const basisNote = planBasisNote(rebalancePlan, timerRun);
  const heldSummary = heldBuySummary(rebalancePlan);
  const liveBandInfo = bandHeadline(rebalancePlanLive?.execution_band);
  const liveRungs: RebalanceRung[] = rebalancePlanLive?.available ? (rebalancePlanLive.rungs ?? []).filter((r) => r.action !== "hold") : [];
  // The cause split decomposes a gap with TODAY's attribution; when the checklist trades a different (15:15) target set,
  // splitting its gaps by today's steps would invent reasons.
  const causesAllowed = !(rebalancePlan?.basis === "timer_run" && (timerRun?.differences?.length ?? 0) > 0);
  const targetAdjustments = activeAdjustments(targetAttribution);
  const principalBreachRows: PrincipalBreach[] = targetAttribution?.principal_defense?.inputs?.breaches ?? [];
  // The ballast headline names the credit state only; say so whenever something else moved the targets.
  const headlineNote = (() => {
    if (!targetAttribution || targetAttribution.error) return null;
    const adoption = targetAttribution.adoption;
    if (targetAttribution.hold || adoption?.reason === "v3_hold_data_unavailable") {
      return "Holding prior targets: the regime engine's inputs are unavailable. See Why These Targets.";
    }
    if (adoption && !adoption.decomposable) {
      return "Holding prior targets while a regime change is confirmed. See Why These Targets.";
    }
    if (targetAdjustments.length === 0) return null;
    return `Targets changed by ${targetAdjustments.map((s) => s.label).join(", ")}. This label reflects the credit gate only. See Why These Targets.`;
  })();

  const durationYears = toNum(portfolioRisk?.estimated_duration_years);
  const durationBudget = toNum(portfolioRisk?.duration_budget_years);
  const durationOverBudget =
    durationYears != null && durationBudget != null ? durationYears > durationBudget + 0.01 : false;
  // (tone now derives from held duration — see heldDurationTone below)

  const rateTrendApplied = Boolean(rateTrend?.applied);
  const rateTrendTone: "gray" | "green" | "amber" = !rateTrend?.enabled
    ? "gray"
    : rateTrendApplied
    ? "amber"
    : "green";

  // A zero-cap lockout rides in `breaches` so the engine keeps the 0% cap in force; count it separately so the
  // tile does not call a locked, already-sold rung a breach.
  const principalBreachEntries = Object.values(principalDefense?.breaches ?? {});
  const principalLocked = principalBreachEntries.filter((b) => (b?.reasons ?? []).includes("zero_cap_lockout")).length;
  const principalBreaches = principalBreachEntries.length - principalLocked;
  const principalDefenseApplied = Boolean(principalDefense?.applied);
  const principalTone: "gray" | "green" | "amber" = !principalDefense?.enabled
    ? "gray"
    : principalDefenseApplied || principalBreaches > 0 || principalLocked > 0
    ? "amber"
    : "green";

  // FI v3 credit-regime state machine (present only when the recraft config is active)
  const creditRegime = meta?.credit_regime && meta.credit_regime.active ? meta.credit_regime : null;
  const v3 = creditRegime ? v3StateDisplay(creditRegime.state) : null;
  // The ballast state is named once, in the headline — tone rides on the headline itself
  // instead of a chip repeating the same word. Mirrors the chip tone -> text-color mapping
  // in components/cockpit so the headline keeps the semantics the chip carried.
  const v3HeadingClass = (t?: ChipTone): string =>
    t === "positive"
      ? "text-up"
      : t === "warning"
      ? "text-warn"
      : t === "elevated"
      ? "text-elevated"
      : t === "danger"
      ? "text-down"
      : t === "info"
      ? "text-info"
      : "";
  const wideCredit = creditRegime?.signals?.wide_credit;
  const v3RateTrend = creditRegime?.signals?.rate_trend;
  const v3Rearm = creditRegime?.signals?.rearm;
  // Explicit tile states (WI-4): Rising / Falling / Held in deadband / Unavailable — never "Rising" for no data.
  const wideTile = wideCreditTile(wideCredit);
  const rateTile = rateTrendTile(v3RateTrend, {
    dgs10Last: creditRegime?.fetched?.dgs10_last ?? null,
    detailedState: creditRegime?.state ?? null,
    source: creditRegime?.lineage?.source ?? null,
  });
  const rearmView = rearmTile(v3Rearm);
  const signalLineage = lineageView(creditRegime?.lineage);
  const tapeLine = tapeLineText(creditRegime?.tape_10y_indicative);
  const v3DurationCap = toNum(creditRegime?.duration_cap_years ?? undefined);

  // ---- Governing duration cap (display basis) ----
  // The backend publishes the cap the engine ENFORCES (portfolio_risk.enforced_duration_cap_years,
  // 2026-09-14, punch list 2a): the state's cap, tightened by capital preservation when VIX is elevated.
  // Held duration is scored against that one. The fallback is the 2026-08-18 interim (item 2b) for a
  // payload that predates it: the tighter of the two configured caps, both named on screen.
  // Display-only: no cap value is changed and nothing is enforced here.
  const enforcedDurationCap = toNum(portfolioRisk?.enforced_duration_cap_years ?? undefined);
  const governingDurationCap =
    enforcedDurationCap ??
    (durationBudget != null && v3DurationCap != null
      ? Math.min(durationBudget, v3DurationCap)
      : durationBudget ?? v3DurationCap ?? null);
  const durationCapsDisagree =
    enforcedDurationCap == null &&
    durationBudget != null &&
    v3DurationCap != null &&
    Math.abs(durationBudget - v3DurationCap) > 0.01;
  const durationCapNote =
    enforcedDurationCap != null
      ? capNote({
          enforced: enforcedDurationCap,
          regime: toNum(portfolioRisk?.regime_duration_cap_years ?? undefined),
          tightenedBy: portfolioRisk?.duration_cap_tightened_by ?? null,
          unknown: Boolean(portfolioRisk?.duration_cap_capital_preservation_unknown),
          regimeKey: portfolioRisk?.regime ?? null,
        })
      : durationCapsDisagree && durationBudget != null && v3DurationCap != null
      ? `Caps disagree: budget ${durationBudget.toFixed(2)}y vs regime ${v3DurationCap.toFixed(2)}y — showing the tighter.`
      : null;

  // ---- FI v3 cockpit derived values ----
  const roleMap = portfolioRisk?.instrument_roles ?? {};
  const roleOf = (etf: string) => String(roleMap[etf]?.principal_role ?? "other");

  // Duration actually HELD. portfolio_risk.estimated_duration_years is built from the engine's
  // TARGET weights — build_fixed_income_risk_telemetry(targets, ...) in get_fixed_income_snapshot
  // — which is the correct input for the engine's own construction check (the duration budget
  // shapes the targets it recommends) but is NOT the rate risk sitting on the book. Recompute
  // from current weights using the same per-ticker durations the engine publishes, so the cap
  // can be read against real exposure as well as against the proposed roster.
  // Prefer the backend figure: it sees ALL holdings and can price an off-roster rung from the
  // measured profile, whereas instrument_roles only covers tickers in the target set. The local
  // computation stays as a fallback so the page still reports something if the API predates it.
  const heldDurationApi = toNum(portfolioRisk?.held_duration_years ?? undefined);
  const heldCoverageApi = portfolioRisk?.held_duration_coverage;
  const durationOf = (etf: string) => toNum(roleMap[etf]?.duration_years);
  const heldRungs = weightsRowsAll.filter((r) => r.current > 0.00005);
  // Basis: held weights only, and only if at least ONE held rung has a published duration.
  // Without that guard `durationOf(...) ?? 0` returned 0.00y for a fully unpriced book, which
  // rendered DV01 = $0 and an all-zero shock ladder — rate risk shown as nil (item 10).
  const heldRungsPriced = heldRungs.filter((r) => durationOf(r.etf) != null);
  const heldDurationLocal = heldRungsPriced.length
    ? heldRungs.reduce((a, r) => a + r.current * (durationOf(r.etf) ?? 0), 0)
    : undefined;
  const heldDurationYears = heldDurationApi ?? heldDurationLocal;
  const heldDurationUnpriced =
    heldCoverageApi?.missing_tickers ?? heldRungs.filter((r) => durationOf(r.etf) == null).map((r) => r.etf);
  // A rung we hold with no published duration (e.g. a legacy ticker mid roster-transition)
  // drags the sum DOWN. Never present that as the whole number — flag it as a floor instead.
  const heldDurationComplete =
    heldCoverageApi?.all_covered ?? (heldRungs.length > 0 && heldDurationUnpriced.length === 0);
  // "≥" asserts a FLOOR, which is only true of the local path: it sums unpriced rungs as 0, so
  // the real figure can only be higher. The backend normalises over COVERED weight instead
  // (fixed_income_logic.build_held_duration_telemetry), implicitly assigning an excluded rung the
  // covered average — the true value can land either side, so a floor claim there would be wrong.
  const heldDurationIsFloor = heldDurationApi == null && !heldDurationComplete;
  const heldDurationPrefix = heldDurationIsFloor ? "≥ " : "";
  const heldDurationBasisNote = heldDurationComplete
    ? ""
    : heldDurationIsFloor
    ? " — floor; unpriced held rungs counted as zero duration"
    : " — partial coverage; estimated over the rungs with a published duration";
  // Basis: held duration vs the GOVERNING (tighter published) cap — see governingDurationCap.
  const heldOverCap =
    heldDurationYears != null && governingDurationCap != null
      ? heldDurationYears > governingDurationCap + 0.01
      : false;
  const heldDurationTone: "gray" | "green" | "amber" =
    heldDurationYears == null || governingDurationCap == null ? "gray" : heldOverCap ? "amber" : "green";
  const durationGap =
    heldDurationYears != null && durationYears != null ? heldDurationYears - durationYears : null;
  // $ rate risk is exposure on real money — held duration ONLY. No fallback to the target
  // roster: substituting it printed a populated DV01 and shock ladder under a "Duration held"
  // row already showing "—", with a tooltip claiming a held basis (item 10).
  const riskDurationYears = heldDurationYears;
  // Gauge basis: held duration over the governing cap. Null (not a target-based percentage)
  // when held duration is unavailable, so the bar is not drawn rather than silently rescaled.
  const heldDurationCapPct =
    governingDurationCap != null && governingDurationCap > 0 && heldDurationYears != null
      ? heldDurationYears / governingDurationCap
      : null;

  // #5 time-in-state
  const daysInState = creditRegime?.since_utc
    ? Math.max(0, Math.floor((Date.now() - Date.parse(creditRegime.since_utc)) / 86400000))
    : null;
  // #4 gate context — where the spread sits in its own history
  const gatePct = toNum(wideCredit?.current_percentile ?? undefined);

  // #1 roster-transition checklist: target vs current, in $ (>= $500 material)
  // Basis: unfiltered rows — the checklist is the whole book, not the table's current search.
  // With a published share plan (2026-09-14) the dollars come from the SAME backend computation as the
  // whole-share counts, so a rung's dollars and shares cannot disagree; diff x NAV is the fallback for a
  // payload without one. Each rung's gap is split into its causes only when the attribution describes the
  // targets actually returned (never while prior targets are held).
  const transitionRows = (
    planRungs.length
      ? planRungs
          .filter((r) => r.action !== "hold")
          .map((r) => ({ etf: r.ticker, dollars: r.gap_dollars, rung: r as RebalanceRung | undefined }))
      : weightsRowsAll
          .filter((r) => r.diffDollar != null && Math.abs(r.diffDollar) >= 500)
          .map((r) => ({ etf: r.etf, dollars: r.diffDollar as number, rung: undefined as RebalanceRung | undefined }))
  )
    .map((r) => ({
      ...r,
      causes: r.rung && causesAllowed ? gapCauses(targetAttribution, r.etf, r.rung.current_weight, planMarketValue) : null,
    }))
    .sort((a, b) => Math.abs(b.dollars) - Math.abs(a.dollars));
  const buys = transitionRows.filter((r) => r.dollars > 0);
  const sells = transitionRows.filter((r) => r.dollars < 0);
  // With a published execution band, "on target" means the tested strategy would not trade; otherwise the older
  // 2% sum-of-drift display gate stands.
  const onTarget = bandInfo ? !bandInfo.trade : (driftAbsSum ?? 1) < 0.02;
  const shareSummary = planSummary(rebalancePlan);

  // #3 risk summary
  const creditLegs = ["VCIT", "JAAA"];
  const creditPct = creditLegs.reduce((a, t) => a + (toNum(meta?.target_weights?.[t]) ?? 0), 0);
  const dv01 =
    riskDurationYears != null && typeof nav === "number" ? riskDurationYears * nav * 0.0001 : undefined;

  // #2 sleeve composition — group by the engine's own principal_role (etf_risk_profile in
  // fixed_income_config.json, delivered via instrument_roles). This is the same field the Role
  // column in the Weights table below shows, so the two always agree. Deliberately NOT a
  // hardcoded bucket list in the frontend: a ticker whose role we don't recognise still gets
  // its own group rather than silently vanishing from a card that reads as a full 100%.
  // Pure grouping in src/lib/fiSleeveComposition.ts (CIO 2026-10-23: per-ticker gaps inside a role,
  // and 0%/0% roles kept on the card, dimmed, instead of vanishing).
  // Basis: unfiltered rows — a card that reads as a full 100% of the sleeve must not be
  // reshaped by the Weights search box.
  const sleeveComposition = buildSleeveComposition(weightsRowsAll, roleOf);

  // Credit spread exposure cuts ACROSS roles: the engine's principal_drawdown_overlay
  // credit_tickers (and duration_budget.reduction_order_credit) are VCIT + JAAA, but JAAA's
  // principal_role is calm_carry — it carries spread risk with ~0.06y duration. Surface the
  // total so the role split and the "Credit exposure" tile don't read as contradicting.
  // Basis: meta.current_weights, unfiltered — mirrors how creditPct reads meta.target_weights.
  // Reading the filtered table rows let the Weights search box drive this to 0.0% current
  // against a live target (item 6).
  const creditCur = creditLegs.reduce((a, t) => a + (toNum(meta?.current_weights?.[t]) ?? 0), 0);
  const creditSpanRoles = creditLegs.filter((t) =>
    weightsRowsAll.some((r) => r.etf === t && (r.target > 0.0005 || r.current > 0.0005)),
  );

  // #1 sleeve yield (estimated carry)
  const fiYield = meta?.fi_yield ?? null;
  const sleeveYield = toNum(fiYield?.sleeve_yield_pct ?? undefined);
  const perEtfYield = (fiYield?.per_etf_yield_pct ?? {}) as Record<string, number>;
  // #3 duration contribution by rung
  const durContribOf = (etf: string) => toNum(portfolioRisk?.duration_contribution_years?.[etf]);
  // #2 rate-shock ladder: first-order $ P&L at parallel shifts (convexity excluded)
  const rateShockLadder =
    riskDurationYears != null && typeof nav === "number"
      ? [-100, -50, 50, 100, 200].map((bp) => ({ bp, pnl: -riskDurationYears * (bp / 10000) * nav }))
      : [];

  if (userLoading) {
    return (
      <Layout>
        <p style={{ color: ui.textPrimary }}>Loading...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }} className="px-2 py-3">
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b pb-3 mb-4"
          style={{ borderColor: ui.cardBorder }}
        >
          <div>
            <h2 className="text-2xl font-semibold" style={{ color: ui.textPrimary }}>📈 Fixed Income Targets</h2>
            <p className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" style={smallMutedText} data-testid="fi-data-asof">
              {freshness.map((f, i) => (
                <span key={f.key} className="whitespace-nowrap" data-freshness={f.key} data-stale={f.stale ? "1" : "0"}>
                  {i > 0 && <span className="mr-2" style={{ color: ui.textMuted }}>•</span>}
                  {f.label}:{" "}
                  <span className="font-medium" style={{ color: ui.textSecondary }}>
                    {f.value == null ? "—" : f.kind === "timestamp" ? fmtUtc(f.value) : fmtObsDate(f.value)}
                  </span>
                  {f.stale && <span className={`${chipClass("warning")} ml-1`}>{f.staleNote ?? "stale"}</span>}
                </span>
              ))}
              <span className="whitespace-nowrap" data-testid="fi-refresh-status">
                <span className="mr-2" style={{ color: ui.textMuted }}>•</span>
                Page Refreshed: <span className="font-medium" style={{ color: ui.textSecondary }}>{fmtUtc(asOfUtc)}</span>
                {" · "}
                {autoRefresh ? `auto every ${Math.round(refreshEverySec / 60)} min, paused while the tab is hidden` : "auto-refresh off"}
              </span>
            </p>
            {fredBanner && (
              <p
                className={`text-xs mt-1 ${fredBanner.level === "problem" ? "text-down font-medium" : "text-warn font-medium"}`}
                data-testid="fi-fred-source"
                data-level={fredBanner.level}
              >
                {fredBanner.text}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={syncIbkrAndRefresh}
              disabled={loading}
              title="Pulls the latest FI positions and NAV from IBKR into the dashboard, then refreshes. Does not place, modify, or cancel any orders."
            >
              {loading ? "Syncing…" : "Sync IBKR + Refresh"}
            </Button>

            <Button size="sm" variant="outline" onClick={() => fetchFISnapshot()} disabled={loading}>
              Refresh
            </Button>

            <div className="flex items-center gap-2 rounded px-2 py-1" style={themeInputStyle}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-xs" style={secondaryText}>Auto</span>
              <select
                className="text-xs rounded px-1 py-0.5"
                style={themeInputStyle}
                value={refreshEverySec}
                onChange={(e) => setRefreshEverySec(Number(e.target.value))}
                disabled={!autoRefresh}
                title="The inputs change about four times a day; Sync IBKR + Refresh pulls new marks on demand."
              >
                <option value={900}>15m</option>
                <option value={1800}>30m</option>
                <option value={3600}>60m</option>
              </select>
            </div>

            <Button size="sm" variant="outline" onClick={() => setShowTelemetry((s) => !s)}>
              {showTelemetry ? "Hide Telemetry" : "Show Telemetry"}
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setMethodologyOpen(true)}
              title="How to read this board — what every FI tile means, how it is computed, and what it deliberately does not do."
            >
              Methodology
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              disabled={!meta}
              title="Download the FI weights / transition table (ETF, role, est. yield, duration contribution, current/target, drift) to a local CSV for records."
            >
              Export CSV
            </Button>
          </div>
        </div>

        {loading && <p className="italic mb-3" style={secondaryText}>Loading FI data…</p>}
        {/* Was a raw #dc2626 -- theme-blind, and one of the last hardcoded colours left
            after the 2026-07-28 centralisation. */}
        {error && <p className="mb-4" style={{ color: "var(--status-danger-text)" }}>Error: {error}</p>}
        {/* Separate from `error` on purpose: fetchFISnapshot clears `error` as it starts,
            so a staleness warning written just before a reload would never be seen. */}
        {syncNote && <p className="mb-1 text-sm" style={secondaryText}>{syncNote}</p>}
        {syncWarn && <p className="mb-4" style={{ color: "var(--status-warning-text)" }}>{syncWarn}</p>}

        {meta && (
          <>
            {creditRegime && v3 && (
              <div className="mb-5">
                <div className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>
                  Ballast posture — credit-regime state machine
                </div>
                <div className={CARD_CLASS} style={themeCardStyle}>
                  <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
                    <div className="lg:w-1/3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-2xl font-semibold ${v3HeadingClass(v3.tone)}`}
                          style={v3HeadingClass(v3.tone) ? undefined : { color: ui.textPrimary }}
                        >
                          {v3.label}
                        </span>
                        {creditRegime.held && <Pill tone="amber">Holding (data)</Pill>}
                      </div>
                      <p className="text-sm mt-1" style={secondaryText}>{v3.blurb}</p>
                      {headlineNote && (
                        <p className="text-xs mt-1 text-warn" data-testid="fi-headline-adjustments">{headlineNote}</p>
                      )}
                      {daysInState != null && (
                        <p className="text-xs mt-1" style={smallMutedText}>
                          {daysInState === 0
                            ? "Entered this state today"
                            : `${daysInState} day${daysInState === 1 ? "" : "s"} in this state`}
                          {creditRegime.since_utc ? ` · since ${new Date(creditRegime.since_utc).toLocaleDateString()}` : ""}
                        </p>
                      )}
                      {creditRegime.explain && (
                        <p className="text-xs mt-2" style={smallMutedText}>{creditRegime.explain}</p>
                      )}
                      {creditRegime.hold_reason && (
                        <p className="text-xs mt-1" style={{ color: "#d97706" }}>Hold: {creditRegime.hold_reason}</p>
                      )}
                    </div>

                    <div className="lg:flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                        <p className="text-xs" style={smallMutedText}>Wide-Credit Gate</p>
                        <p
                          className="text-base font-semibold mt-0.5"
                          data-testid="fi-tile-wide-credit"
                          data-state={wideTile.state}
                          style={{ color: wideTile.state === "on" || wideTile.state === "unavailable" ? "#d97706" : ui.textPrimary }}
                        >
                          {wideTile.headline}
                        </p>
                        <p className="text-[11px] mt-1" style={secondaryText}>{wideTile.detail}</p>
                        {gatePct != null && (
                          <p className="text-[11px]" style={smallMutedText}>
                            {(gatePct * 100).toFixed(0)}th pct of history · gate at 80th
                          </p>
                        )}
                      </div>

                      <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                        <p className="text-xs" style={smallMutedText} title={signalLineage.tooltip || undefined}>Rate Trend (10Y)</p>
                        <p
                          className="text-base font-semibold mt-0.5"
                          data-testid="fi-tile-rate-trend"
                          data-state={rateTile.state}
                          style={{ color: rateTile.state === "unavailable" ? "#d97706" : ui.textPrimary }}
                        >
                          {rateTile.headline}
                        </p>
                        <p className="text-[11px] mt-1" style={secondaryText}>{rateTile.detail}</p>
                        {rateTile.flavor && <p className="text-[11px]" style={secondaryText}>{rateTile.flavor}</p>}
                        <p className="text-[10px] mt-1" style={smallMutedText} data-testid="fi-rate-trend-asof">
                          {rateTile.asOf} · {rateTile.nextUpdate}
                        </p>
                        {tapeLine && (
                          <p className="text-[10px] mt-1 italic" style={smallMutedText} data-testid="fi-rate-trend-tape">{tapeLine}</p>
                        )}
                      </div>

                      <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                        <p className="text-xs" style={smallMutedText}>Held Duration vs Cap</p>
                        {/* Held duration scored against the GOVERNING (tighter) cap — width and
                            colour now come from the same cap; the bar previously took its width
                            from the regime cap and its colour from the duration budget. */}
                        <p className="text-base font-semibold mt-0.5" style={{ color: ui.textPrimary }}>
                          {heldDurationYears != null ? `${heldDurationPrefix}${heldDurationYears.toFixed(2)}y` : "—"}
                          <span className="text-xs font-normal" style={secondaryText}> / {governingDurationCap != null ? `${governingDurationCap.toFixed(2)}y` : "—"}</span>
                        </p>
                        <div
                          className="mt-2 h-1.5 rounded"
                          style={{ backgroundColor: "rgba(148,163,184,0.25)" }}
                          title={heldDurationCapPct == null ? "Held duration or cap unavailable — bar not drawn" : undefined}
                        >
                          {heldDurationCapPct != null && (
                            <div
                              className="h-1.5 rounded"
                              style={{
                                width: `${Math.min(100, Math.max(0, heldDurationCapPct * 100)).toFixed(0)}%`,
                                backgroundColor: heldOverCap ? "#f59e0b" : "#3b82f6",
                              }}
                            />
                          )}
                        </div>
                        {durationCapNote && (
                          <p className="text-[10px] mt-1 text-warn">{durationCapNote}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] mt-3" style={smallMutedText} data-testid="fi-rearm-line" data-state={rearmView.state}>
                    Re-arm watch: {rearmView.headline} · {rearmView.detail}
                    {creditRegime.fetched?.baa10y_last ? ` · FRED BAA10Y ${creditRegime.fetched.baa10y_last} close` : ""}
                    <span className="mx-2" style={{ color: ui.textMuted }}>•</span>
                    Advisory only — targets are recommendations; trades executed manually.
                  </p>
                  <p className="text-[10px] mt-1" style={smallMutedText} title={signalLineage.tooltip || undefined} data-testid="fi-signal-lineage" data-status={signalLineage.status}>
                    {signalLineage.summary}
                  </p>
                  {signalLineage.issues.map((c) => (
                    <p
                      key={c.key}
                      className={`text-[11px] mt-0.5 ${c.level === "problem" ? "text-warn font-medium" : "text-warn"}`}
                      data-testid={`fi-signal-lineage-${c.key}`}
                    >
                      {c.level === "problem" ? "⚠ " : ""}{c.note}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {creditRegime && targetAttribution && (
              <div className="mb-5">
                <WhyTheseTargets attribution={targetAttribution} targetDurationYears={durationYears ?? null} />
              </div>
            )}

            {creditRegime && (
              <div className="mb-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className={`${CARD_CLASS} lg:col-span-2`} style={themeCardStyle}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Roster Transition to Target</p>
                    <Pill tone={onTarget ? "green" : "amber"}>
                      {onTarget
                        ? bandInfo
                          ? "Within band"
                          : "On target"
                        : `${transitionRows.length} rung${transitionRows.length === 1 ? "" : "s"} to trade`}
                    </Pill>
                  </div>
                  {timerBanner && (
                    <p
                      className="text-xs mt-2 rounded px-2 py-1"
                      style={{ backgroundColor: ui.groupBg, color: "var(--status-warning-text)" }}
                      data-testid="fi-timer-banner"
                    >
                      {timerBanner}
                    </p>
                  )}
                  {bandInfo && (
                    <p
                      className="text-sm mt-2 font-medium"
                      style={{ color: ui.textPrimary }}
                      data-testid="fi-band-headline"
                      data-trade={bandInfo.trade ? "1" : "0"}
                    >
                      {bandInfo.text}
                    </p>
                  )}
                  {basisNote && (
                    <p className="text-[11px] mt-1" style={smallMutedText} data-testid="fi-plan-basis">
                      {basisNote}
                    </p>
                  )}
                  {transitionRows.length === 0 || (onTarget && !bandInfo) ? (
                    <p className="text-sm mt-2" style={secondaryText}>Holdings are within tolerance of today's targets.</p>
                  ) : (
                    <div
                      className={`mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4${bandInfo && !bandInfo.trade ? " opacity-60" : ""}`}
                      data-testid="fi-transition-rows"
                      data-informational={bandInfo && !bandInfo.trade ? "1" : "0"}
                    >
                      {bandInfo && !bandInfo.trade && (
                        <p className="sm:col-span-2 text-[11px]" style={smallMutedText}>
                          Informational only: inside the execution band these lines are not a trade instruction.
                        </p>
                      )}
                      {([
                        ["Buy", "#16a34a", buys, "text-up"],
                        ["Sell", "#dc2626", sells, "text-down"],
                      ] as const).map(([side, color, rows, toneClass]) => (
                        <div key={side}>
                          <p className={`${MICRO_LABEL} mb-1`} style={{ color }}>{side}</p>
                          {rows.length === 0 ? (
                            <p className="text-xs" style={smallMutedText}>—</p>
                          ) : (
                            rows.map((r) => (
                              <div key={r.etf} className="py-1" data-testid={`fi-rung-${r.etf}`}>
                                <div className="flex items-center justify-between text-sm">
                                  <span style={{ color: ui.textPrimary }}>{r.etf} <span className="text-[11px]" style={smallMutedText}>{roleOf(r.etf)}</span></span>
                                  <span className={`${toneClass} font-medium`}>{fmtSignedDollar(r.dollars)}</span>
                                </div>
                                {r.rung && (
                                  <p
                                    className="text-[11px]"
                                    style={r.rung.action === "no_price" ? { color: "var(--status-warning-text)" } : secondaryText}
                                    data-testid={`fi-shares-${r.etf}`}
                                    title={r.rung.note ?? rebalancePlan?.price_basis ?? undefined}
                                  >
                                    {sharesLine(r.rung)}
                                  </p>
                                )}
                                {r.rung?.wash_sale_hold && r.rung.action === "buy" && (
                                  <p className="text-[11px]" style={smallMutedText} data-testid={`fi-hold-${r.etf}`}>
                                    If not held: {sharesLineRaw(r.rung)}. Sale detected {r.rung.wash_sale_hold.sale_detected_date} by the
                                    15:15 run; detection can lag the actual sale by a day.
                                  </p>
                                )}
                                {r.causes && r.causes.length > 0 && (
                                  <p className="text-[11px]" style={smallMutedText} data-testid={`fi-causes-${r.etf}`}>
                                    {r.causes.map((c) => `${c.label} ${fmtSignedDollar(c.dollars)}`).join(" · ")}
                                  </p>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {shareSummary && transitionRows.length > 0 && (!onTarget || bandInfo) && (
                    <p className="text-[11px] mt-3" style={secondaryText} data-testid="fi-share-summary">
                      {shareSummary}
                      {rebalancePlan?.oldest_price_updated_utc ? ` Marks as of ${fmtUtc(rebalancePlan.oldest_price_updated_utc)}.` : ""}
                    </p>
                  )}
                  {heldSummary && (
                    <p className="text-[11px] mt-2" style={secondaryText} data-testid="fi-held-summary">
                      {heldSummary}
                    </p>
                  )}
                  <p className="text-[11px] mt-2" style={smallMutedText}>
                    Advisory checklist to the targets of the 15:15 MT engine run (or today's live targets until a run is recorded).
                    Trade only when the band headline says so, and trade every listed rung to target, as the tested strategy did.
                    Trades are entered manually in IBKR, and marks are not live quotes, so re-check prices in TWS. Positive = add,
                    negative = trim; ETFs with no market value show against a target of 0.
                  </p>
                  {rebalancePlanLive && rebalancePlan?.basis === "timer_run" && (
                    <details className="mt-2" data-testid="fi-intraday-plan">
                      <summary className="text-[11px] cursor-pointer" style={secondaryText}>
                        Intraday view (information, not the checklist): live-refresh targets
                        {liveBandInfo ? ` · ${liveBandInfo.text}` : ""}
                      </summary>
                      {liveRungs.length === 0 ? (
                        <p className="text-[11px] mt-1" style={smallMutedText}>No rung outside tolerance against the live targets.</p>
                      ) : (
                        <ul className="text-[11px] mt-1 space-y-0.5" style={secondaryText}>
                          {liveRungs.map((r) => (
                            <li key={r.ticker} data-testid={`fi-intraday-${r.ticker}`}>
                              {r.ticker}: {sharesLine(r)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </details>
                  )}
                </div>

                <div className={CARD_CLASS} style={themeCardStyle}>
                  <p className="text-sm font-semibold mb-2" style={{ color: ui.textPrimary }}>Sleeve Risk &amp; Carry</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span style={smallMutedText}>Est. Sleeve Carry</span>
                      <span
                        className="font-medium"
                        data-testid="fi-sleeve-carry"
                        data-stale={fiYield?.staleness?.stale ? "1" : "0"}
                        style={sleeveYield == null || fiYield?.staleness?.stale ? smallMutedText : { color: "#16a34a" }}
                        title={fiYield?.anchor_date ? `Estimated weighted yield — live FRED benchmarks, anchored to reported yields (oldest as of ${fiYield.anchor_date}). All roster rungs with a calibrated benchmark are included, STIP among them. ${stalenessTitle(fiYield.staleness)}` : undefined}
                      >
                        {sleeveYield != null ? `${sleeveYield.toFixed(2)}%` : "—"}
                        {fiYield?.staleness?.stale && <span className="ml-1 text-[10px] font-semibold text-warn">STALE</span>}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span style={smallMutedText}>Duration Held / Cap</span>
                      {/* Denominator = governing (tighter published) cap, same basis as the gauge above. */}
                      <span
                        style={{ color: ui.textPrimary }}
                        title={`Target roster duration: ${durationYears != null ? `${durationYears.toFixed(2)}y` : "—"}${durationCapNote ? ` · ${durationCapNote}` : ""}`}
                      >
                        {heldDurationYears != null ? `${heldDurationPrefix}${heldDurationYears.toFixed(2)}y` : "—"}
                        <span style={secondaryText}> / {governingDurationCap != null ? `${governingDurationCap.toFixed(2)}y` : "—"}</span>
                      </span>
                    </div>
                    {durationCapNote && (
                      <p className="text-[10px] -mt-1 text-warn">{durationCapNote}</p>
                    )}
                    <div className="flex justify-between text-sm">
                      <span style={smallMutedText}>DV01 (per +1bp)</span>
                      {/* Held duration only — never the target roster (item 10). "—" when held is unavailable. */}
                      <span
                        style={{ color: ui.textPrimary }}
                        title={
                          dv01 != null
                            ? `Held duration x FI NAV x 1bp${heldDurationBasisNote}`
                            : "Held duration or FI NAV unavailable — not shown on the target roster's duration"
                        }
                      >
                        {dv01 != null ? fmtDollar(dv01) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span style={smallMutedText}>Credit Exposure</span>
                      <span style={{ color: ui.textPrimary }} title={`${creditLegs.join(" + ")} — current vs target`}>
                        {fmtPct(creditCur)}
                        <span style={secondaryText}> / {fmtPct(creditPct)} tgt</span>
                      </span>
                    </div>
                  </div>
                  {/* Ladder is held-duration only. With held duration unavailable it used to be
                      computed off the TARGET roster and printed under a held-basis caption; now
                      the cells read "—" and say why (item 10). */}
                  {rateShockLadder.length === 0 && (
                    <div className="mt-3">
                      <p className="text-xs mb-1" style={smallMutedText}>Rate Shock — P&amp;L at Parallel Shifts</p>
                      <div className="grid grid-cols-5 gap-1 text-center">
                        {[-100, -50, 50, 100, 200].map((bp) => (
                          <div key={bp} className="rounded px-1 py-1.5" style={{ backgroundColor: ui.groupBg }}>
                            <div className="text-[10px]" style={smallMutedText}>{bp > 0 ? `+${bp}` : bp}bp</div>
                            <div className="text-[11px] font-medium" style={smallMutedText}>—</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] mt-1 text-warn">
                        Held duration or FI NAV unavailable — not shown on the target roster's duration.
                      </p>
                    </div>
                  )}
                  {rateShockLadder.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs mb-1" style={smallMutedText}>Rate Shock — P&amp;L at Parallel Shifts</p>
                      <div className="grid grid-cols-5 gap-1 text-center">
                        {rateShockLadder.map((s) => (
                          <div key={s.bp} className="rounded px-1 py-1.5" style={{ backgroundColor: ui.groupBg }}>
                            <div className="text-[10px]" style={smallMutedText}>{s.bp > 0 ? `+${s.bp}` : s.bp}bp</div>
                            <div className={`text-[11px] font-medium ${s.pnl < 0 ? "text-down" : "text-up"}`}>{fmtSignedDollar(s.pnl)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] mt-3" style={smallMutedText}>First-order rate sensitivity on FI notional; convexity excluded. Carry is an estimate.</p>
                </div>
              </div>
            )}

            <div className="mb-5">
              <div className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>Posture</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {creditRegime && v3 ? (
                // Ballast state is already stated once, in the posture card above — this slot
                // carries the sleeve's size instead of repeating the regime word.
                <div className={CARD_CLASS} style={themeCardStyle}>
                  <p className={MICRO_LABEL} style={smallMutedText}>FI Notional (MV)</p>
                  <p className="text-lg font-semibold mt-1" style={{ color: ui.textPrimary }}>{fmtDollar(nav)}</p>
                  <p className="text-xs mt-1" style={secondaryText}>Market value of the fixed income sleeve</p>
                </div>
              ) : (
                <div className={CARD_CLASS} style={themeCardStyle}>
                  <p className={MICRO_LABEL} style={smallMutedText}>Regime</p>
                  <p
                    className={`text-lg font-semibold mt-1 ${
                      meta.regime?.includes("Risk-Off")
                        ? "text-down"
                        : meta.regime?.includes("Risk-On")
                        ? "text-up"
                        : meta.regime?.includes("Flight")
                        ? "text-info"
                        : "text-warn"
                    }`}
                  >
                    {meta.regime ?? "—"}
                  </p>
                  <p className="text-xs mt-1" style={secondaryText}>
                    FI Notional (MV): <span className="font-medium" style={{ color: ui.textPrimary }}>{fmtDollar(nav)}</span>
                  </p>
                </div>
              )}

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>Cash Reserve</p>
                <p className="text-lg font-semibold mt-1" style={{ color: ui.textPrimary }}>{fmtDollar(cashOverrideState.reserved || observedCash)}</p>
                <p className="text-xs mt-1" style={secondaryText}>
                  {cashOverrideState.enabled ? "Discretionary reserve active" : "Observed broker cash"}
                </p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>FI Active / Target</p>
                <p className="text-lg font-semibold mt-1" style={{ color: ui.textPrimary }}>
                  {fiActualTotalNavPct != null ? `${(fiActualTotalNavPct * 100).toFixed(1)}% / 60.0%` : "—"}
                </p>
                <p className="text-xs mt-1" style={secondaryText}>
                  {fiTargetFillPct != null ? `Target fill ${(fiTargetFillPct * 100).toFixed(1)}%` : "Active allocation vs strategic target"}
                </p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={`${MICRO_LABEL} flex items-center gap-2 flex-wrap`} style={smallMutedText}>
                  <span>
                    Inflation State <span className="text-[11px]" style={smallMutedText}>(breakevens)</span>
                  </span>
                  <Pill tone={infl.tone as any}>{infl.short}</Pill>
                </p>
                <p
                  className={`text-lg font-semibold ${
                    (meta.inflation_state || "").includes("Surge")
                      ? "text-down"
                      : (meta.inflation_state || "").includes("Watch")
                      ? "text-warn"
                      : ""
                  }`}
                  style={
                    !(meta.inflation_state || "").includes("Surge") &&
                    !(meta.inflation_state || "").includes("Watch")
                      ? { color: ui.textPrimary }
                      : undefined
                  }
                  title={
                    meta.inflation_telemetry?.enabled
                      ? `10y BE ${fmtNumber(meta.inflation_telemetry.t10yie_bps)} bps; 5y5y confirm ${yesNo(
                          meta.inflation_telemetry.t5y5y_confirm
                        )}`
                      : undefined
                  }
                >
                  {meta.inflation_state ?? "Inflation-Neutral"}
                </p>
                {showTipsLine && (
                  <p className="text-xs mt-1" style={secondaryText}>
                    {tipTicker} target: {fmtPct(tipTarget)} · current: {fmtPct(tipCurrent)}
                  </p>
                )}
              </div>

              </div>

              <div className={`${MICRO_LABEL} mt-5 mb-2`} style={smallMutedText}>Market inputs</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>VIX</p>
                <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>z {fmtNumber(meta.vix_zscore)}</p>
                <p className="text-xs mt-1" style={secondaryText}>spot {fmtNumber(vixSpot)}</p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>MOVE (bps)</p>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>{fmtNumber(moveBps)}</p>
                  {typeof movePct === "number" && (
                    <span className={chipClass(stressToneForPercentile(movePct))}>
                      {(() => {
                        const b = stressBandForPercentile(movePct);
                        return b === "extreme" ? "Extreme" : b === "high" ? "High" : b === "elevated" ? "Elevated" : "Normal";
                      })()}
                    </span>
                  )}
                </div>
                {typeof movePct === "number" && (
                  <p className="text-xs mt-1" style={smallMutedText}>Percentile: {(movePct * 100).toFixed(0)}%</p>
                )}
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>Yield Curve 10s–2s (bps)</p>
                <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>{fmtNumber(curveBps)}</p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className={MICRO_LABEL} style={smallMutedText}>IG OAS (bps)</p>
                <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>{fmtNumber(oasBps)}</p>
              </div>

              </div>

              <div className={`${MICRO_LABEL} mt-5 mb-2`} style={smallMutedText}>Overlays &amp; budget</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={CARD_CLASS} style={themeCardStyle} title={momTooltip}>
                <p className="text-sm flex items-center gap-2" style={smallMutedText}>
                  Momentum <Pill tone={momTone}>{momLabel}</Pill>
                </p>
                <p className="text-lg font-medium mt-1" style={{ color: ui.textPrimary }}>{momEnabled ? "Enabled" : "Disabled"}</p>
                <p className="text-xs mt-1" style={secondaryText}>
                  Triggers fired: <span className="font-medium" style={{ color: ui.textPrimary }}>{momEnabled ? triggersFired : "—"}</span>
                  <span className="mx-2" style={{ color: ui.textMuted }}>|</span>
                  Scale:{" "}
                  <span className="font-medium" style={{ color: ui.textPrimary }}>
                    {typeof mom?.scale === "number" ? mom.scale.toFixed(2) : "—"}
                  </span>
                </p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className="text-sm flex items-center gap-2" style={smallMutedText}>
                  Duration Budget{" "}
                  {/* Held-vs-cap pill and the cap printed below both read the GOVERNING (tighter)
                      cap, so this card and the cockpit gauge can no longer disagree. "Target over"
                      stays on duration_budget_years — that is the engine's own construction check
                      on the roster it recommends. */}
                  <Pill tone={heldOverCap || durationOverBudget ? "amber" : heldDurationTone}>
                    {heldOverCap ? "Held over" : durationOverBudget ? "Target over budget" : heldDurationYears != null && governingDurationCap != null ? "OK" : "n/a"}
                  </Pill>
                </p>
                <p className="text-lg font-medium mt-1" style={{ color: ui.textPrimary }}>
                  {heldDurationYears != null ? `${heldDurationPrefix}${heldDurationYears.toFixed(2)}y` : "—"}
                  <span className="text-sm font-normal" style={secondaryText}>
                    {governingDurationCap != null ? ` / ${governingDurationCap.toFixed(2)}y cap` : ""}
                  </span>
                </p>
                {durationCapNote && (
                  <p className="text-[11px] mt-1 text-warn">{durationCapNote}</p>
                )}
                <p className="text-xs mt-1" style={secondaryText}>
                  Held duration vs the principal-first cap. Target roster:{" "}
                  {durationYears != null ? `${durationYears.toFixed(2)}y` : "—"}
                  {durationGap != null && Math.abs(durationGap) >= 0.005
                    ? ` (${durationGap > 0 ? "+" : ""}${durationGap.toFixed(2)}y vs held)`
                    : ""}
                </p>
                {!heldDurationComplete && heldDurationUnpriced.length > 0 && (
                  <p className="text-[11px] mt-1 text-down">
                    Understated — no published duration for {heldDurationUnpriced.join(", ")}
                  </p>
                )}
                {heldDurationComplete && (heldCoverageApi?.fallback_tickers?.length ?? 0) > 0 && (
                  <p className="text-[11px] mt-1" style={smallMutedText} title={heldCoverageApi?.note ?? undefined}>
                    Off-roster holding priced from the measured profile:{" "}
                    {(heldCoverageApi?.fallback_tickers ?? []).join(", ")}
                  </p>
                )}
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className="text-sm flex items-center gap-2" style={smallMutedText}>
                  Rate Trend <Pill tone={rateTrendTone}>{rateTrendApplied ? "Defensive" : rateTrend?.enabled ? "Neutral" : "Off"}</Pill>
                </p>
                <p className="text-lg font-medium mt-1" style={{ color: ui.textPrimary }}>
                  {typeof rateTrend?.change_short_bps === "number" ? `${rateTrend.change_short_bps.toFixed(0)} bps` : "—"}
                </p>
                <p className="text-xs mt-1" style={secondaryText}>
                  {rateTrend?.short_window_days ?? 30}d change · scale {typeof rateTrend?.scale === "number" ? rateTrend.scale.toFixed(2) : "—"}
                </p>
              </div>

              <div className={CARD_CLASS} style={themeCardStyle}>
                <p className="text-sm flex items-center gap-2" style={smallMutedText}>
                  Principal Defense <Pill tone={principalTone}>{principalDefenseApplied ? "Capped" : principalDefense?.enabled ? "OK" : "Off"}</Pill>
                </p>
                <p className="text-lg font-medium mt-1" style={{ color: ui.textPrimary }} data-testid="fi-principal-tile-count">
                  {[
                    principalBreaches > 0 ? `${principalBreaches} breach${principalBreaches === 1 ? "" : "es"}` : "No breaches",
                    principalLocked > 0 ? `${principalLocked} locked` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {principalBreachRows.length > 0 && (
                  <p className="text-xs mt-1" style={{ color: ui.textPrimary }} data-testid="fi-principal-tile-breaches">
                    {principalBreachRows
                      .map(
                        (b) =>
                          b.lockout
                            ? `${b.ticker} locked through ${b.locked_through ?? "—"} (lockout after a stop sale to zero)`
                            : `${b.ticker} ${b.return_pct != null ? `${(b.return_pct * 100).toFixed(2)}%` : "—"} vs ${(b.limit_pct * 100).toFixed(1)}%` +
                          (b.capped ? ` → capped to ${((b.cap_weight ?? 0) * 100).toFixed(1)}%` : " (no weight moved)"),
                      )
                      .join(" · ")}
                  </p>
                )}
                <p className="text-xs mt-1" style={secondaryText}>
                  Caps a duration/credit ETF whose price-only loss vs cost basis passes its limit. It does not change the regime label.
                  {principalDefense?.zero_cap_lockout?.days
                    ? ` A 0%-capped rung it sells to zero stays at 0% for ${principalDefense.zero_cap_lockout.days} calendar days.`
                    : ""}
                  {principalDefense?.zero_cap_lockout?.conflicts?.length
                    ? ` Re-bought during its lockout: ${principalDefense.zero_cap_lockout.conflicts.join(", ")}.`
                    : ""}
                </p>
              </div>
            </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Pill tone="blue">Target drift</Pill>
              <span className="text-sm" style={secondaryText}>
                Sum abs diff: <span className="font-medium" style={{ color: ui.textPrimary }}>{fmtPct(driftAbsSum)}</span>
              </span>
              <span className="text-xs" style={smallMutedText}>
                (This is an allocation drift gauge for monitoring—not a trading instruction.)
              </span>
              {timerBanner && (
                <span className="text-xs" style={{ color: "var(--status-warning-text)" }} data-testid="fi-live-note-drift">
                  Measured against live intraday targets, not the 15:15 MT run the checklist trades.
                </span>
              )}
            </div>

            {showTelemetry && (
              <div className="mb-6 p-4 rounded border" style={{ ...themeCardStyle, boxShadow: ui.shadow }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Overlay Telemetry</p>
                    {momEnabled ? <Pill tone={momTone}>{momLabel}</Pill> : <Pill tone="gray">Disabled</Pill>}
                  </div>
                  <p className="text-xs" style={smallMutedText}>Delta-of-delta early drift check (non-regime-flipping)</p>
                </div>

                <p className={`${MICRO_LABEL} mt-4 mb-2`} style={smallMutedText}>Momentum overlay</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                    <p className="text-xs" style={smallMutedText}>Credit Spread ROC (bps)</p>
                    <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                      {typeof mom?.credit_spread_roc_bps === "number" ? mom.credit_spread_roc_bps.toFixed(1) : "—"}
                    </p>
                    <p className="text-[11px] mt-1" style={secondaryText}>3-month change (approx {mom?.roc_trading_days ?? 63} trading days)</p>
                  </div>

                  <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                    <p className="text-xs" style={smallMutedText}>Curve Slope Change (bps)</p>
                    <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                      {typeof mom?.curve_slope_change_bps === "number" ? mom.curve_slope_change_bps.toFixed(1) : "—"}
                    </p>
                    <p className="text-[11px] mt-1" style={secondaryText}>Negative = flattening</p>
                  </div>

                  <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                    <p className="text-xs" style={smallMutedText}>MOVE Percentile Momentum</p>
                    <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                      {typeof mom?.move_pct_momentum === "number" ? `${(mom.move_pct_momentum * 100).toFixed(0)} pts` : "—"}
                    </p>
                    <p className="text-[11px] mt-1" style={secondaryText}>
                      Current pct:{" "}
                      {typeof mom?.move_pct_current === "number" ? `${(mom.move_pct_current * 100).toFixed(0)}%` : "—"}
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>Trigger flags</p>
                  <div className="flex flex-wrap gap-2">
                    <Pill tone={triggers?.credit_spread_momentum_negative ? "amber" : "gray"}>
                      Credit widening: {triggers ? (triggers.credit_spread_momentum_negative ? "Yes" : "No") : "—"}
                    </Pill>
                    <Pill tone={triggers?.curve_flattening_accelerating ? "amber" : "gray"}>
                      Curve flatten accel: {triggers ? (triggers.curve_flattening_accelerating ? "Yes" : "No") : "—"}
                    </Pill>
                    <Pill tone={triggers?.move_rising_from_bottom_decile ? "amber" : "gray"}>
                      MOVE rising from bottom decile: {triggers ? (triggers.move_rising_from_bottom_decile ? "Yes" : "No") : "—"}
                    </Pill>
                  </div>

                  {mom?.deltas_applied && (
                    <div className="mt-4">
                      <p className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>Applied deltas</p>
                      <div className="text-xs" style={{ color: ui.textPrimary }}>
                        {(Object.entries(mom.deltas_applied) as Array<[string, number]>).map(([k, vRaw]) => {
                          const v = toNum(vRaw) ?? 0;
                          return (
                            <span key={k} className="inline-flex items-center mr-3">
                              <span className="font-medium">{k}</span>
                              <span className={`ml-1 ${v >= 0 ? "text-up" : "text-down"}`}>{fmtPct(v)}</span>
                            </span>
                          );
                        })}
                      </div>
                      <p className="text-[11px] mt-2" style={smallMutedText}>
                        These are small nudges only (BKLN ↓, VGSH/PULS ↑) and do not change regime classification.
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-4 border-t" style={{ borderColor: ui.cardBorder }}>
                  <p className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>Principal-first overlays</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                      <p className="text-xs" style={smallMutedText}>Estimated Duration — Held / Target</p>
                      <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                        {heldDurationYears != null ? `${heldDurationPrefix}${heldDurationYears.toFixed(2)}` : "—"}
                        <span className="text-sm font-normal" style={secondaryText}>
                          {" / "}{durationYears != null ? `${durationYears.toFixed(2)} years` : "—"}
                        </span>
                      </p>
                      {/* Raw telemetry: name BOTH published caps rather than the budget alone —
                          the tiles above score held duration against the tighter of the two. */}
                      <p className="text-[11px] mt-1" style={secondaryText}>
                        Budget: {durationBudget != null ? `${durationBudget.toFixed(2)} years` : "—"}
                        {" · "}Regime cap: {v3DurationCap != null ? `${v3DurationCap.toFixed(2)} years` : "—"}
                        {" · "}Enforced: {enforcedDurationCap != null ? `${enforcedDurationCap.toFixed(2)} years` : "not published"}
                        {governingDurationCap != null ? ` · governing (shown on tiles): ${governingDurationCap.toFixed(2)} years` : ""}
                      </p>
                    </div>
                    <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                      <p className="text-xs" style={smallMutedText}>10Y Rate Trend</p>
                      <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                        {typeof rateTrend?.change_long_bps === "number" ? `${rateTrend.change_long_bps.toFixed(1)} bps` : "—"}
                      </p>
                      <p className="text-[11px] mt-1" style={secondaryText}>Long window: {rateTrend?.long_window_days ?? 60} days</p>
                    </div>
                    <div className="p-3 rounded" style={{ backgroundColor: ui.groupBg }}>
                      <p className="text-xs" style={smallMutedText}>Principal Defense State</p>
                      <p className="text-base font-medium" style={{ color: ui.textPrimary }}>
                        {principalDefense?.state ?? "—"}
                      </p>
                      <p className="text-[11px] mt-1" style={secondaryText}>Caps applied: {principalDefense?.caps_applied ? Object.keys(principalDefense.caps_applied).join(", ") || "None" : "None"}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {creditRegime && sleeveComposition.length > 0 && (
              <div className="mb-5">
                <div className={`${MICRO_LABEL} mb-2`} style={smallMutedText}>Sleeve composition — current vs target by ballast role</div>
                {timerBanner && (
                  <p className="text-xs mb-2" style={{ color: "var(--status-warning-text)" }} data-testid="fi-live-note-composition">
                    Targets here are live intraday targets, not the 15:15 MT run the checklist trades.
                  </p>
                )}
                <div className={CARD_CLASS} style={themeCardStyle}>
                  <div className="space-y-2.5">
                    {sleeveComposition.map((c) => {
                      const toneClass = c.diff > 0.0025 ? "text-up" : c.diff < -0.0025 ? "text-down" : "";
                      const curPct = Math.max(0, Math.min(100, c.cur * 100));
                      const tgtPct = Math.max(0, Math.min(100, c.tgt * 100));
                      const gapTone = (d: number) => (d > 0.0025 ? "text-up" : d < -0.0025 ? "text-down" : "");
                      return (
                        <div
                          key={c.key}
                          data-testid={`fi-role-${c.key}`}
                          data-inactive={c.inactive ? "1" : "0"}
                          style={c.inactive ? { opacity: 0.55 } : undefined}
                        >
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <span style={{ color: ui.textPrimary }}>
                              {c.label} <span className="text-[11px]" style={smallMutedText}>{c.etfs.join(" · ")}</span>
                              {c.inactive && (
                                <span className="text-[11px] ml-2 italic" style={smallMutedText} data-testid={`fi-role-note-${c.key}`}>
                                  {inactiveRoleNote(creditRegime?.state, v3?.label, c.residual)}
                                </span>
                              )}
                            </span>
                            <span className="flex items-baseline gap-2 whitespace-nowrap">
                              <span className="font-medium" style={{ color: ui.textPrimary }} title="Current share of FI notional">
                                {fmtPct(c.cur)}
                              </span>
                              <span className="text-[11px]" style={smallMutedText} title="Target share of FI notional">
                                / {fmtPct(c.tgt)} tgt
                              </span>
                              <span
                                className={`text-[11px] ${toneClass}`}
                                style={toneClass ? undefined : smallMutedText}
                                title={
                                  c.diffDollar == null
                                    ? "Target minus current. FI notional (NAV) unavailable — cannot compute the dollar gap."
                                    : "Target minus current"
                                }
                              >
                                {c.diff > 0 ? "+" : ""}
                                {fmtPct(c.diff)}
                                {c.diffDollar != null ? ` · ${fmtSignedDollar(c.diffDollar)}` : ""}
                              </span>
                            </span>
                          </div>
                          <div className="relative mt-1 h-2 rounded" style={{ backgroundColor: "rgba(148,163,184,0.20)" }}>
                            <div className="h-2 rounded" style={{ width: `${curPct.toFixed(1)}%`, backgroundColor: "#3b82f6" }} />
                            <div
                              className="absolute w-[2px]"
                              style={{
                                left: `${tgtPct.toFixed(1)}%`,
                                marginLeft: "-1px",
                                top: "-3px",
                                height: "14px",
                                backgroundColor: ui.textSecondary,
                              }}
                              title={`Target ${fmtPct(c.tgt)}`}
                            />
                          </div>
                          {c.members.length > 0 && (
                            <p className="text-[11px] mt-1" style={smallMutedText} data-testid={`fi-role-gaps-${c.key}`}>
                              {c.members.map((m, i) => {
                                const tone = gapTone(m.diff);
                                return (
                                  <span
                                    key={m.etf}
                                    title={`${m.etf}: ${fmtPct(m.cur)} current / ${fmtPct(m.tgt)} target${
                                      m.diffDollar != null ? ` · ${fmtSignedDollar(m.diffDollar)}` : ""
                                    }`}
                                  >
                                    {i > 0 && " · "}
                                    {m.etf}{" "}
                                    <span className={tone} style={tone ? undefined : smallMutedText}>
                                      {m.diff > 0 ? "+" : ""}
                                      {fmtPct(m.diff)}
                                    </span>
                                  </span>
                                );
                              })}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] mt-3" style={smallMutedText}>
                    Filled bar = where the sleeve actually sits today; the tick marks the target. Both are shares of FI notional, rolled
                    up from the Weights table below. A positive gap means the role is under target (buy); negative means over target (sell).
                    Roles are the engine's own <span className="font-mono">principal_role</span> assignments, so they match the Role column below.
                    The small line under a multi-ticker role is each ticker's own gap, which the role's net figure can hide; dimmed roles
                    are 0% current and 0% target now but are used by the engine in other states.
                  </p>
                  {creditSpanRoles.length > 1 && (
                    <p className="text-[11px] mt-1.5" style={smallMutedText}>
                      Credit spread exposure cuts across these roles: {creditSpanRoles.join(" + ")} ={" "}
                      <span style={{ color: ui.textSecondary }}>{fmtPct(creditCur)} current / {fmtPct(creditPct)} target</span>
                      {" "}— the figure in the Credit exposure tile. JAAA sits in Calm Carry because it is AAA floating-rate
                      (~0.06y duration), but the engine treats it as a credit ticker for drawdown caps and de-risking order.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Weights</p>
                {inflEtf && <Pill tone="amber">Overlay ETF: {inflEtf}</Pill>}
                {timerBanner && (
                  <span className="text-xs" style={{ color: "var(--status-warning-text)" }} data-testid="fi-live-note-weights">
                    Target % = live intraday targets, not the 15:15 MT run the checklist trades.
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  placeholder="Filter ETF…"
                  className="rounded px-2 py-1 text-sm w-44"
                  style={themeInputStyle}
                  value={weightsFilter}
                  onChange={(e) => setWeightsFilter(e.target.value)}
                />
                <span className="text-xs" style={smallMutedText}>
                  Sort: <span className="font-medium" style={{ color: ui.textSecondary }}>{sortKey} {sortDir}</span>
                </span>
              </div>
            </div>

            <div className="overflow-x-auto mb-8">
              <Table
                className="min-w-full rounded text-sm"
                style={{
                  backgroundColor: ui.tableBg,
                  color: ui.textPrimary,
                  border: `1px solid ${ui.cardBorder}`,
                  boxShadow: ui.shadow,
                }}
              >
                <TableHeader style={{ backgroundColor: ui.tableHeaderBg, color: ui.tableHeaderText }}>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("etf")}
                    >
                      ETF
                    </TableHead>
                    {creditRegime && (
                      <>
                        <TableHead style={{ color: ui.tableHeaderText }}>Role</TableHead>
                        <TableHead style={{ color: ui.tableHeaderText }} title="Estimated carry at TARGET weight, not the held weight">Yield (Est, Tgt)</TableHead>
                        <TableHead style={{ color: ui.tableHeaderText }} title="Duration contribution at TARGET weight, not the held weight">Dur Contrib (Tgt)</TableHead>
                      </>
                    )}
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("current")}
                    >
                      Current %
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("target")}
                    >
                      Target %
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("diff")}
                    >
                      Diff %
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("diff")}
                      title="Dollar drift = Diff % × FI Notional. Positive = under target (add), negative = excess (trim)."
                    >
                      Diff $
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      style={{ color: ui.tableHeaderText }}
                      onClick={() => toggleSort("notionalAbs")}
                    >
                      Current Notional
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {weightsRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={creditRegime ? 9 : 6} className="italic text-center" style={secondaryText}>
                        No ETFs match the filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    weightsRows.map((r) => (
                      <TableRow
                        key={r.etf}
                        title={inflEtf === r.etf ? "Inflation overlay ETF" : undefined}
                        style={inflEtf === r.etf ? { backgroundColor: "rgba(251, 191, 36, 0.10)" } : undefined}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = ui.hoverBg;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            inflEtf === r.etf ? "rgba(251, 191, 36, 0.10)" : "transparent";
                        }}
                      >
                        <TableCell className="font-medium" style={{ color: ui.textPrimary }}>{r.etf}</TableCell>
                        {creditRegime && (
                          <>
                            <TableCell className="text-xs capitalize" style={secondaryText}>{roleOf(r.etf).replace(/_/g, " ")}</TableCell>
                            <TableCell className="text-xs" style={secondaryText}>{perEtfYield[r.etf] != null ? `${perEtfYield[r.etf].toFixed(2)}%` : "—"}</TableCell>
                            <TableCell className="text-xs" style={secondaryText}>{durContribOf(r.etf) != null ? `${(durContribOf(r.etf) as number).toFixed(2)}y` : "—"}</TableCell>
                          </>
                        )}
                        <TableCell style={{ color: ui.textPrimary }}>{fmtPct(r.current)}</TableCell>
                        <TableCell style={{ color: ui.textPrimary }}>{fmtPct(r.target)}</TableCell>
                        <TableCell
                          className={r.diff > 0.0025 ? "text-up" : r.diff < -0.0025 ? "text-down" : ""}
                          style={r.diff <= 0.0025 && r.diff >= -0.0025 ? secondaryText : undefined}
                        >
                          {fmtPct(r.diff)}
                        </TableCell>
                        <TableCell
                          className={r.diff > 0.0025 ? "text-up" : r.diff < -0.0025 ? "text-down" : ""}
                          style={
                            r.diffDollar == null
                              ? smallMutedText
                              : r.diff <= 0.0025 && r.diff >= -0.0025
                              ? secondaryText
                              : undefined
                          }
                          title={r.diffDollar == null ? "FI notional (NAV) unavailable — cannot compute dollar drift" : undefined}
                        >
                          {fmtSignedDollar(r.diffDollar)}
                        </TableCell>
                        <TableCell
                          style={r.marketValue == null ? smallMutedText : { color: ui.textPrimary }}
                          title={r.marketValue == null ? "No MarketValue found for this ticker in SQL holdings" : undefined}
                        >
                          {r.marketValue == null ? "—" : fmtDollar(r.marketValue)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <IncomeCalendarCard enabled={!userLoading} />
          </>
        )}
      </div>

      <RiskMethodologyModal
        open={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        content={FIXED_INCOME_METHODOLOGY}
        titleId="fixed-income-methodology-title"
      />
    </Layout>
  );
};

export default FixedIncomeView;